import useCurrentUser from "@/hooks/useCurrentUser";
import apiClient from "@/lib/apiClient";
import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { z } from "zod";

const UseReactionSchema = z.object({
  elementId: z.coerce.number(),
});

type UseReactionOptions = z.infer<typeof UseReactionSchema>;

const useReaction = (options: UseReactionOptions) => {
  const { elementId } = UseReactionSchema.parse(options);

  const { currentUser } = useCurrentUser();

  const userUid = currentUser?.uid;

  const { data, error, mutate } = useSWR(
    `/reactions/${elementId}/user`,
    async (url) => {
      return apiClient.get(url, userUid);
    }
  );

  // Get the reaction from the data
  const reaction = data?.reaction;

  const { trigger: createReaction, isMutating: isCreating } = useSWRMutation(
    "reactions/create",
    async (key, { arg }: { arg: { type: "positive" | "negative" } }) => {
      // Optimistically update the local data
      mutate(
        (currentData) => ({
          ...currentData,
          reaction: { type: arg.type, uid: "temp-uid" },
        }),
        { revalidate: false }
      );

      const result = await apiClient.post(
        `/reactions/${elementId}`,
        userUid,
        arg
      );

      // Update with the actual data from the server
      mutate();

      return result;
    }
  );

  const { trigger: undoReaction, isMutating: isUndoing } = useSWRMutation(
    "reactions/undo",
    async (key, { arg: reactionUid }: { arg: string }) => {
      // Optimistically update the local data
      mutate(
        (currentData) => ({
          ...currentData,
          reaction: null,
        }),
        { revalidate: false }
      );

      const result = await apiClient.delete(
        `/reactions/${elementId}/${reactionUid}`,
        userUid
      );

      // Update with the actual data from the server
      mutate();

      return result;
    }
  );

  const toggleReaction = useCallback(
    async (type: "positive" | "negative") => {
      if (reaction && reaction.type === type) {
        await undoReaction(reaction.uid);
      } else {
        await createReaction({ type });
      }
    },
    [reaction, createReaction, undoReaction]
  );

  return {
    reaction,
    toggleReaction,
    undoReaction,
    createReaction,
    isCreating,
    isUndoing,
    error,
  };
};

export default useReaction;
